import { useEffect, useRef } from 'react'
import { playerNormal, getPlayerTheta, getPlayerPhi, getPlayerYaw } from '@/state/playerPosition'
import { getVillagePlacements } from '@/interaction/InteractionDetector'
import { VILLAGE_PROFILES } from '@/economy/types'
import type { VillageProfileType } from '@/economy/types'
import { useShipStore } from '@/state/useShipStore'
import './Compass.css'

const PROFILE_ICONS: Record<VillageProfileType, string> = {
  farming: '\u{1F33E}',
  mining: '\u{26CF}',
  forestry: '\u{1FAB5}',
  port: '\u{2693}',
  mountain: '\u{26F0}',
}

const STRIP_WIDTH = 400
const PIXELS_PER_DEGREE = 3
const MAX_VILLAGES = 8
const MAX_ANGULAR_DIST = 1.2

// Degree marks: every 15 degrees gets a label, every 5 gets a tick
const CARDINALS: Record<number, string> = {
  0: 'N', 45: 'NE', 90: 'E', 135: 'SE',
  180: 'S', 225: 'SW', 270: 'W', 315: 'NW',
}

export function Compass() {
  const aboard = useShipStore((s) => s.aboard)
  const trackRef = useRef<HTMLDivElement>(null)
  const markerPoolRef = useRef<HTMLDivElement[]>([])

  // Build static tick/label elements + marker pool
  useEffect(() => {
    const track = trackRef.current
    if (!track) return

    // Clear any previous children
    while (track.firstChild) track.removeChild(track.firstChild)

    // Create ticks and labels for 0-359 degrees
    for (let deg = 0; deg < 360; deg += 5) {
      const tick = document.createElement('div')
      tick.className = deg % 15 === 0 ? 'compass__tick compass__tick--major' : 'compass__tick compass__tick--minor'
      track.appendChild(tick)

      if (deg % 15 === 0) {
        const label = document.createElement('span')
        const cardinal = CARDINALS[deg]
        if (cardinal) {
          label.className = `compass__label compass__label--cardinal${deg === 0 ? ' compass__label--n' : ''}`
          label.textContent = cardinal
        } else {
          label.className = 'compass__label'
          label.textContent = String(deg)
        }
        track.appendChild(label)
      }
    }

    // Create marker pool
    const pool: HTMLDivElement[] = []
    for (let i = 0; i < MAX_VILLAGES; i++) {
      const el = document.createElement('div')
      el.className = 'compass__marker'
      el.style.display = 'none'
      const icon = document.createElement('span')
      icon.className = 'compass__marker-icon'
      el.appendChild(icon)
      track.appendChild(el)
      pool.push(el)
    }
    markerPoolRef.current = pool

    return () => {
      pool.forEach((el) => el.remove())
    }
  }, [])

  // RAF loop — direct DOM writes
  useEffect(() => {
    let running = true
    let raf = 0

    function tick() {
      if (!running) return
      const track = trackRef.current
      if (!track) { raf = requestAnimationFrame(tick); return }

      const villages = getVillagePlacements()
      const pTheta = getPlayerTheta()
      const pPhi = getPlayerPhi()
      const yaw = getPlayerYaw()

      const sp = Math.sin(pPhi), cp = Math.cos(pPhi)
      const st = Math.sin(pTheta), ct = Math.cos(pTheta)
      const northX = cp * st, northY = -sp, northZ = cp * ct
      const eastX = ct, eastZ = -st
      const pnx = playerNormal.x, pny = playerNormal.y, pnz = playerNormal.z

      // Player heading in degrees (0=N, 90=E, etc.)
      // yaw is the camera/player yaw angle
      let headingDeg = (yaw * 180 / Math.PI) % 360
      if (headingDeg < 0) headingDeg += 360

      const halfStrip = STRIP_WIDTH / 2

      // Position all ticks and labels
      let tickIdx = 0
      const children = track.children
      for (let deg = 0; deg < 360; deg += 5) {
        // How far is this degree from center heading?
        let diff = deg - headingDeg
        // Wrap to [-180, 180]
        if (diff > 180) diff -= 360
        if (diff < -180) diff += 360

        const px = halfStrip + diff * PIXELS_PER_DEGREE

        // Tick element
        const tickEl = children[tickIdx] as HTMLElement
        if (tickEl) {
          tickEl.style.left = `${px.toFixed(1)}px`
          tickEl.style.display = (px >= -10 && px <= STRIP_WIDTH + 10) ? '' : 'none'
        }
        tickIdx++

        // Label element (every 15 degrees)
        if (deg % 15 === 0) {
          const labelEl = children[tickIdx] as HTMLElement
          if (labelEl) {
            labelEl.style.left = `${px.toFixed(1)}px`
            labelEl.style.display = (px >= -20 && px <= STRIP_WIDTH + 20) ? '' : 'none'
          }
          tickIdx++
        }
      }

      // Village markers
      interface MarkerData { bearingDeg: number; distance: number; profile: VillageProfileType }
      const results: MarkerData[] = []

      for (let i = 0; i < villages.length; i++) {
        const vn = villages[i].normal
        const dot = pnx * vn.x + pny * vn.y + pnz * vn.z
        const angDist = Math.acos(Math.max(-1, Math.min(1, dot)))
        if (angDist > MAX_ANGULAR_DIST) continue

        const dx = vn.x - dot * pnx
        const dy = vn.y - dot * pny
        const dz = vn.z - dot * pnz
        const dLen = Math.sqrt(dx * dx + dy * dy + dz * dz)
        if (dLen < 1e-8) continue

        const projNorth = (dx * northX + dy * northY + dz * northZ) / dLen
        const projEast = (dx * eastX + dz * eastZ) / dLen
        const bearing = Math.atan2(projEast, projNorth)

        let bearingDeg = (bearing * 180 / Math.PI) % 360
        if (bearingDeg < 0) bearingDeg += 360

        const profile = i < VILLAGE_PROFILES.length
          ? VILLAGE_PROFILES[i].profile
          : 'farming' as VillageProfileType

        results.push({ bearingDeg, distance: angDist, profile })
      }

      results.sort((a, b) => a.distance - b.distance)

      const pool = markerPoolRef.current
      for (let i = 0; i < pool.length; i++) {
        const el = pool[i]
        if (i >= results.length) {
          el.style.display = 'none'
          continue
        }
        const m = results[i]
        let diff = m.bearingDeg - headingDeg
        if (diff > 180) diff -= 360
        if (diff < -180) diff += 360

        const px = halfStrip + diff * PIXELS_PER_DEGREE
        if (px < -20 || px > STRIP_WIDTH + 20) {
          el.style.display = 'none'
          continue
        }

        const closeness = 1 - m.distance / MAX_ANGULAR_DIST
        el.style.display = ''
        el.style.left = `${px.toFixed(1)}px`
        el.style.opacity = String(0.5 + closeness * 0.5)

        const icon = el.firstChild as HTMLSpanElement
        const newIcon = PROFILE_ICONS[m.profile]
        if (icon.textContent !== newIcon) icon.textContent = newIcon
      }

      raf = requestAnimationFrame(tick)
    }

    raf = requestAnimationFrame(tick)
    return () => {
      running = false
      cancelAnimationFrame(raf)
    }
  }, [])

  if (aboard) return null

  return (
    <div className="compass">
      <div className="compass__strip">
        <div className="compass__track" ref={trackRef} />
      </div>
    </div>
  )
}
